/**
 * Coplanar boundary loop helpers — pure functions for the Surface Inset
 * operator. Three concerns:
 *
 *   1. `extractFaceRegionBoundaryLoop` — walk the perimeter of a coplanar face
 *      region and return it as an ordered CCW polygon in the face's basis-UV
 *      plane.
 *   2. `simplifyCoplanarLoop` — drop collinear runs (subdivided sides,
 *      T-junctions). Cross-product test on normalized edges; threshold below
 *      any deliberate corner but well above float noise.
 *   3. `offsetSimplePolygonInward` — geometric polygon offset for simple
 *      polygons. Detects bisector blow-up and self-intersection; clamps the
 *      requested distance to the largest valid value via binary search.
 */
import type { FaceBasis } from "./faceBasis.ts";
import { deriveEdgesFromFaces } from "./editSelection.ts";
import { edgeFaces, getAdjacencyCache } from "./topology.ts";
import type { PolygonTopology } from "./types.ts";

export type LoopUv = readonly (readonly [number, number])[];

const COLLINEAR_EPSILON = 1e-4;
const SELF_INTERSECT_EPSILON = 1e-9;
const BISECTOR_DOT_EPSILON = 1e-6;

// ---------------------------------------------------------------------------
// 1. Boundary loop extraction
// ---------------------------------------------------------------------------

/**
 * Walk the perimeter of a coplanar face region and project into basis UV.
 *
 * A "region-boundary edge" is an edge with exactly one adjacent face in the
 * region. For each such edge we look up the in-region face's corner sequence
 * to determine the walk direction (face-on-the-left → CCW when viewed along
 * the basis normal). Returns `null` if no boundary loop is recoverable.
 */
export function extractFaceRegionBoundaryLoop(
  topology: PolygonTopology,
  regionFaces: ReadonlySet<number> | readonly number[],
  positions: Float32Array,
  basis: FaceBasis,
): LoopUv | null {
  const regionSet =
    regionFaces instanceof Set ? regionFaces : new Set(regionFaces);
  if (regionSet.size === 0) return null;

  const vertexCount = positions.length / 3;
  const cache = getAdjacencyCache(topology, vertexCount);

  // Region edges → filter to those with exactly one in-region adjacent face.
  const regionEdges = deriveEdgesFromFaces(topology, regionSet as Set<number>);
  const regionBoundaryEdges = new Set<number>();
  for (const ei of regionEdges) {
    const adj = edgeFaces(cache, ei);
    let inCount = 0;
    for (let i = 0; i < adj.length; i += 1) {
      if (regionSet.has(adj[i]!)) inCount += 1;
    }
    if (inCount === 1) regionBoundaryEdges.add(ei);
  }
  if (regionBoundaryEdges.size === 0) return null;

  // For each boundary edge: find the in-region face, then the corner pair
  // (vSrc, vDst) along that face's CCW corner order. Stored as
  // `outgoing[vSrc] = ei` so the walker can follow vertex chains.
  const outgoing = new Map<number, number>();
  const edgeDir = new Map<number, [number, number]>();
  for (const ei of regionBoundaryEdges) {
    const adj = edgeFaces(cache, ei);
    let inFace = -1;
    for (let i = 0; i < adj.length; i += 1) {
      if (regionSet.has(adj[i]!)) {
        inFace = adj[i]!;
        break;
      }
    }
    if (inFace < 0) continue;
    const start = topology.offsets[inFace]!;
    const end = topology.offsets[inFace + 1]!;
    const size = end - start;
    for (let k = 0; k < size; k += 1) {
      const ci = start + k;
      if (topology.cornerEdges[ci] === ei) {
        const vSrc = topology.corners[ci]!;
        const vDst = topology.corners[start + ((k + 1) % size)]!;
        edgeDir.set(ei, [vSrc, vDst]);
        outgoing.set(vSrc, ei);
        break;
      }
    }
  }
  if (outgoing.size === 0) return null;

  // Walk: pick any start, follow `outgoing` until back to start. If multiple
  // disjoint loops exist (e.g. a region with a hole), this yields just the
  // loop containing the start vertex. v1 ignores hole loops; the picked face
  // is always on the outer loop.
  const startEi = regionBoundaryEdges.values().next().value!;
  const startPair = edgeDir.get(startEi);
  if (!startPair) return null;
  const startV = startPair[0];
  const visited = new Set<number>();
  const loopVerts: number[] = [];
  let current = startV;
  for (let safety = 0; safety < 100000; safety += 1) {
    if (visited.has(current)) break;
    visited.add(current);
    loopVerts.push(current);
    const outEi = outgoing.get(current);
    if (outEi === undefined) break;
    const dir = edgeDir.get(outEi);
    if (!dir) break;
    current = dir[1];
    if (current === startV) break;
  }
  if (loopVerts.length < 3) return null;

  // Project to basis UV.
  const ox = basis.origin[0]!;
  const oy = basis.origin[1]!;
  const oz = basis.origin[2]!;
  const tx = basis.tangent[0]!;
  const ty = basis.tangent[1]!;
  const tz = basis.tangent[2]!;
  const bx = basis.bitangent[0]!;
  const by = basis.bitangent[1]!;
  const bz = basis.bitangent[2]!;
  const out: [number, number][] = [];
  for (const vi of loopVerts) {
    const px = positions[vi * 3]! - ox;
    const py = positions[vi * 3 + 1]! - oy;
    const pz = positions[vi * 3 + 2]! - oz;
    out.push([px * tx + py * ty + pz * tz, px * bx + py * by + pz * bz]);
  }
  return out;
}

// ---------------------------------------------------------------------------
// 2. Collinear simplification
// ---------------------------------------------------------------------------

function loopLengthSqr(ax: number, ay: number): number {
  return ax * ax + ay * ay;
}

function isCollinear(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  epsilon: number,
): boolean {
  const e1x = bx - ax;
  const e1y = by - ay;
  const e2x = cx - bx;
  const e2y = cy - by;
  const len1 = Math.sqrt(loopLengthSqr(e1x, e1y));
  const len2 = Math.sqrt(loopLengthSqr(e2x, e2y));
  if (len1 < BISECTOR_DOT_EPSILON || len2 < BISECTOR_DOT_EPSILON) return true;
  const nx1 = e1x / len1;
  const ny1 = e1y / len1;
  const nx2 = e2x / len2;
  const ny2 = e2y / len2;
  // |sin(turn angle)| via 2D cross of unit edge vectors.
  return Math.abs(nx1 * ny2 - ny1 * nx2) < epsilon;
}

/**
 * Drop vertices that are collinear with their immediate neighbors. Threshold
 * is on `|sin(turn-angle)|` after edge normalization, so the test is
 * scale-invariant. Default `1e-4` (≈0.006°) — well above float noise, well
 * below any deliberate corner including 1024-segment circular boundaries.
 *
 * Multi-pass: collinear chains collapse one per pass. Loops are typically
 * <100 verts so the cost is negligible.
 */
export function simplifyCoplanarLoop(
  loop: LoopUv,
  epsilon: number = COLLINEAR_EPSILON,
): readonly (readonly [number, number])[] {
  if (loop.length < 4) return loop.map((p) => [p[0], p[1]] as const);
  let current: (readonly [number, number])[] = loop.map(
    (p) => [p[0], p[1]] as const,
  );
  for (let pass = 0; pass < loop.length; pass += 1) {
    const n = current.length;
    if (n < 4) break;
    const next: (readonly [number, number])[] = [];
    let dropped = false;
    for (let i = 0; i < n; i += 1) {
      const prev = current[(i - 1 + n) % n]!;
      const curr = current[i]!;
      const succ = current[(i + 1) % n]!;
      // Stop dropping once we'd fall below 3 verts.
      const remainingMin = next.length + (n - i - 1);
      if (
        remainingMin >= 3 &&
        isCollinear(prev[0], prev[1], curr[0], curr[1], succ[0], succ[1], epsilon)
      ) {
        dropped = true;
      } else {
        next.push(curr);
      }
    }
    if (!dropped) break;
    current = next;
  }
  return current;
}

// ---------------------------------------------------------------------------
// 3. Polygon offset (inward)
// ---------------------------------------------------------------------------

function signedArea(loop: readonly (readonly [number, number])[]): number {
  let acc = 0;
  const n = loop.length;
  for (let i = 0; i < n; i += 1) {
    const [x0, y0] = loop[i]!;
    const [x1, y1] = loop[(i + 1) % n]!;
    acc += x0 * y1 - x1 * y0;
  }
  return acc * 0.5;
}

/**
 * Compute the bisector-offset positions for a CCW simple loop without
 * dropping anything. Returns one offset point per input vertex.
 *
 * Returns `null` only when the input is fundamentally undecidable (degenerate
 * input: zero-length input edges, 180° back-fold). Self-intersection /
 * collapsing edges are NOT classified as failure here — that's the caller's
 * job (`offsetWithDropouts`), which iteratively prunes vertices whose
 * adjacent edges have collapsed past zero length.
 */
function bisectorOffsetCcw(
  loop: readonly (readonly [number, number])[],
  distance: number,
): [number, number][] | null {
  const n = loop.length;
  if (n < 3) return null;
  const result: [number, number][] = [];
  for (let i = 0; i < n; i += 1) {
    const prev = loop[(i - 1 + n) % n]!;
    const curr = loop[i]!;
    const succ = loop[(i + 1) % n]!;
    const e1x = curr[0] - prev[0];
    const e1y = curr[1] - prev[1];
    const e2x = succ[0] - curr[0];
    const e2y = succ[1] - curr[1];
    const len1 = Math.sqrt(e1x * e1x + e1y * e1y);
    const len2 = Math.sqrt(e2x * e2x + e2y * e2y);
    if (len1 < BISECTOR_DOT_EPSILON || len2 < BISECTOR_DOT_EPSILON) return null;
    const dx1 = e1x / len1;
    const dy1 = e1y / len1;
    const dx2 = e2x / len2;
    const dy2 = e2y / len2;
    // Inward normal of a CCW edge is the +90° rotation of edge direction.
    const n1x = -dy1;
    const n1y = dx1;
    const n2x = -dy2;
    const n2y = dx2;
    let bx = n1x + n2x;
    let by = n1y + n2y;
    const blen = Math.sqrt(bx * bx + by * by);
    if (blen < BISECTOR_DOT_EPSILON) return null; // 180° back-fold
    bx /= blen;
    by /= blen;
    const sinHalf = bx * n1x + by * n1y;
    if (Math.abs(sinHalf) < BISECTOR_DOT_EPSILON) return null;
    const k = distance / sinHalf;
    result.push([curr[0] + bx * k, curr[1] + by * k]);
  }
  return result;
}

/**
 * Iteratively offset `loop` inward by `distance`, dropping any vertex whose
 * adjacent offset edges have collapsed to non-positive length (the local
 * "edge-event" of a straight skeleton). Re-runs the bisector offset on the
 * pruned polygon until no further collapses occur. The function is pure of
 * `(loop, distance)` so reducing `distance` automatically reinstates dropped
 * vertices — the caller's preview snaps back smoothly.
 *
 * Returns `null` when:
 *   - the polygon collapses below 3 vertices entirely
 *   - the offset self-intersects in a non-edge-event way (split events on
 *     reflex vertices) — split events are out of scope; caller falls back to
 *     binary-search clamping in that case.
 */
function offsetWithDropouts(
  loop: readonly (readonly [number, number])[],
  distance: number,
): [number, number][] | null {
  let active: (readonly [number, number])[] = loop.slice();
  for (let safety = 0; safety < loop.length + 4; safety += 1) {
    if (active.length < 3) return null;
    const offset = bisectorOffsetCcw(active, distance);
    if (!offset) return null;

    // Find the most-collapsed edge (smallest non-positive length, where
    // "non-positive" includes a tight epsilon to absorb FP noise around
    // exact-collapse). Drop one of its endpoints from the *active* polygon
    // and retry; the surviving endpoint takes a fresh bisector against the
    // now-adjacent neighbor.
    let worstLen = Infinity;
    let worstIdx = -1;
    const m = offset.length;
    for (let i = 0; i < m; i += 1) {
      const a = offset[i]!;
      const b = offset[(i + 1) % m]!;
      const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
      if (len < worstLen) {
        worstLen = len;
        worstIdx = i;
      }
    }
    if (worstIdx < 0 || worstLen > BISECTOR_DOT_EPSILON) {
      // No collapsing edges — accept this offset if it's also globally
      // simple. A non-edge-event self-intersection signals a split event
      // (reflex-vertex impact); caller handles via clamping.
      if (selfIntersects(offset)) return null;
      if (signedArea(offset) <= 0) return null;
      return offset;
    }
    // Drop the second endpoint of the collapsed edge from the active
    // polygon (`active[(worstIdx+1) % active.length]`). The choice between
    // dropping endpoint 0 vs 1 is arbitrary for a clean edge-event; both
    // choices produce equivalent offset polygons in the next iteration.
    const dropIdx = (worstIdx + 1) % active.length;
    active = active.filter((_, i) => i !== dropIdx);
  }
  return null;
}

function segmentsCross(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
): boolean {
  const rx = bx - ax;
  const ry = by - ay;
  const sx = dx - cx;
  const sy = dy - cy;
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < SELF_INTERSECT_EPSILON) return false; // parallel
  const tNum = (cx - ax) * sy - (cy - ay) * sx;
  const uNum = (cx - ax) * ry - (cy - ay) * rx;
  const t = tNum / denom;
  const u = uNum / denom;
  return t > SELF_INTERSECT_EPSILON &&
    t < 1 - SELF_INTERSECT_EPSILON &&
    u > SELF_INTERSECT_EPSILON &&
    u < 1 - SELF_INTERSECT_EPSILON;
}

function selfIntersects(loop: readonly (readonly [number, number])[]): boolean {
  const n = loop.length;
  for (let i = 0; i < n; i += 1) {
    const a = loop[i]!;
    const b = loop[(i + 1) % n]!;
    for (let j = i + 2; j < n; j += 1) {
      // Skip the pair that would share endpoint `a`.
      if (i === 0 && j === n - 1) continue;
      const c = loop[j]!;
      const d = loop[(j + 1) % n]!;
      if (segmentsCross(a[0], a[1], b[0], b[1], c[0], c[1], d[0], d[1])) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Offset a simple polygon inward by `distance`. Edges that would collapse
 * to non-positive length are dropped from the polygon (one vertex per
 * dropped edge, mirroring a straight-skeleton edge event), so the result
 * is always a simple polygon with strictly positive edges — no clamping
 * required for the typical case. Reversibility is automatic: the function
 * is pure of `(loop, distance)`, so reducing the distance recomputes
 * the offset against the original polygon and reinstates dropped edges
 * smoothly.
 *
 * For non-convex polygons, a reflex vertex can hit a non-adjacent edge
 * (a "split event") before any edge collapses; the iterative dropout
 * algorithm cannot resolve that. In that case the function falls back to
 * binary-search clamping the distance to the largest split-event-free
 * value and returns `clamped: true`.
 *
 * `clamped` reflects ONLY the split-event fallback. Edge-event dropouts
 * are part of the natural offset and not flagged.
 */
export function offsetSimplePolygonInward(
  loop: LoopUv,
  distance: number,
): { loop: readonly (readonly [number, number])[]; clamped: boolean } {
  if (distance <= 0 || loop.length < 3) {
    return { loop: loop.map((p) => [p[0], p[1]] as const), clamped: false };
  }
  const cw = signedArea(loop) < 0;
  const ccw: (readonly [number, number])[] = cw
    ? loop.slice().reverse().map((p) => [p[0], p[1]] as const)
    : loop.map((p) => [p[0], p[1]] as const);

  const direct = offsetWithDropouts(ccw, distance);
  if (direct) {
    return { loop: cw ? direct.slice().reverse() : direct, clamped: false };
  }

  // Split-event fallback — binary search the largest distance the dropout
  // algorithm produces a valid offset for.
  let lo = 0;
  let hi = distance;
  let best: [number, number][] | null = null;
  for (let iter = 0; iter < 32; iter += 1) {
    const mid = (lo + hi) * 0.5;
    if (mid <= 0) break;
    const r = offsetWithDropouts(ccw, mid);
    if (r) {
      best = r;
      lo = mid;
    } else {
      hi = mid;
    }
  }
  if (!best) {
    return { loop: ccw.map((p) => [p[0], p[1]] as const), clamped: true };
  }
  return { loop: cw ? best.slice().reverse() : best, clamped: true };
}
